import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    apiFetch,
    clearStoredAuth,
    getDiscordAuthorizeUrl,
    getStoredProfile,
    getStoredToken,
    rememberReturnPath,
    storeProfile,
} from "../func/auth";
import { getDiscordAvatarUrl } from "../func/utils";

const STATUS_FILTERS = ["pending", "approved", "denied", "all"];
const ADMIN_TABS = [
    { id: "requests", label: "Requests" },
    { id: "audit", label: "Audit log" },
];
const AUDIT_PAGE_SIZE = 50;

function formatDate(value) {
    if (!value) return "Not reviewed";
    return new Date(value * 1000).toLocaleString();
}

function formatOptionalDate(value) {
    if (!value) return "Any";
    return new Date(value * 1000).toLocaleString();
}

function formatName(item) {
    return item.global_name || item.username || item.discord_id;
}

function formatAuditName(item) {
    return item.global_name || item.username || item.discord_id || "Not signed in";
}

function formatDuration(value) {
    if (value === null || value === undefined) return "Unknown";
    if (value < 1000) return `${value}ms`;
    return `${(value / 1000).toFixed(2)}s`;
}

function countryFlag(code) {
    if (!code || code.length !== 2) return "";
    const upper = code.toUpperCase();
    return upper.replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

function countryName(code) {
    if (!code || code.length !== 2) return "Unknown";
    try {
        const names = new Intl.DisplayNames([navigator.language || "en"], { type: "region" });
        return names.of(code.toUpperCase()) || "Unknown";
    } catch {
        return code.toUpperCase();
    }
}

function userAgentSummary(userAgent) {
    if (!userAgent) return "Unknown user agent";
    return userAgent.length > 140 ? `${userAgent.slice(0, 140)}...` : userAgent;
}

function statusBadge(status) {
    if (status === "approved") {
        return "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300";
    }
    if (status === "denied") {
        return "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300";
    }
    return "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
}

function AdminSignIn({ error, onSignIn }) {
    return (
        <div className="grid min-h-dvh place-items-center bg-slate-50 px-4 py-8 dark:bg-slate-900">
            <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="mb-5 text-3xl font-bold tracking-normal">
                    <span className="text-pink-500">Meow</span>
                    <span className="text-indigo-600 dark:text-indigo-400">Search</span>
                </div>
                <h1 className="text-2xl font-bold tracking-normal text-slate-900 dark:text-white">
                    Admin sign in
                </h1>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Use an admin Discord account to review access requests.
                </p>
                {error && (
                    <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                        {error}
                    </div>
                )}
                <button
                    type="button"
                    onClick={onSignIn}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#4752C4]"
                >
                    <span className="grid h-5 w-5 place-items-center rounded bg-white/15 text-xs">D</span>
                    Sign in with Discord
                </button>
                <Link to="/" className="mt-4 block text-sm font-semibold text-slate-500 transition hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400">
                    Back to search
                </Link>
            </div>
        </div>
    );
}

export default function AdminPage() {
    const [apiToken, setApiToken] = useState(() => getStoredToken());
    const [profile, setProfile] = useState(() => getStoredProfile());
    const [activeTab, setActiveTab] = useState("requests");
    const [requests, setRequests] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditOffset, setAuditOffset] = useState(0);
    const [auditPagination, setAuditPagination] = useState({
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
        total: 0,
        has_next: false,
        has_previous: false,
    });
    const [filter, setFilter] = useState("pending");
    const [loadingProfile, setLoadingProfile] = useState(Boolean(apiToken));
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [loadingAudit, setLoadingAudit] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [error, setError] = useState("");

    const isAdmin = Boolean(profile?.is_admin);

    useEffect(() => {
        if (!apiToken) return;

        let cancelled = false;
        async function loadProfile() {
            setLoadingProfile(true);
            setError("");
            try {
                const payload = await apiFetch("/api/auth/me", { token: apiToken });
                const nextProfile = {
                    authorized: Boolean(payload.authorized),
                    is_admin: Boolean(payload.is_admin),
                    user: payload.user || null,
                    request: payload.request || null,
                };
                storeProfile(nextProfile);
                if (!cancelled) setProfile(nextProfile);
            } catch (err) {
                clearStoredAuth();
                if (!cancelled) {
                    setApiToken(null);
                    setProfile(null);
                    setError(err.message);
                }
            } finally {
                if (!cancelled) setLoadingProfile(false);
            }
        }

        loadProfile();
        return () => {
            cancelled = true;
        };
    }, [apiToken]);

    useEffect(() => {
        if (!apiToken || !isAdmin || activeTab !== "requests") return;

        let cancelled = false;
        async function loadRequests() {
            setLoadingRequests(true);
            setError("");
            try {
                const query = filter === "all" ? "" : `?status=${filter}`;
                const payload = await apiFetch(`/api/auth/admin/access-requests${query}`, { token: apiToken });
                if (!cancelled) setRequests(payload.requests || []);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoadingRequests(false);
            }
        }

        loadRequests();
        return () => {
            cancelled = true;
        };
    }, [apiToken, activeTab, filter, isAdmin]);

    useEffect(() => {
        if (!apiToken || !isAdmin || activeTab !== "audit") return;

        let cancelled = false;
        async function loadAuditLogs() {
            setLoadingAudit(true);
            setError("");
            try {
                const payload = await apiFetch(`/api/auth/admin/search-audit-logs?limit=${AUDIT_PAGE_SIZE}&offset=${auditOffset}`, { token: apiToken });
                if (!cancelled) {
                    setAuditLogs(payload.logs || []);
                    setAuditPagination(payload.pagination || {
                        limit: AUDIT_PAGE_SIZE,
                        offset: auditOffset,
                        total: 0,
                        has_next: false,
                        has_previous: auditOffset > 0,
                    });
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoadingAudit(false);
            }
        }

        loadAuditLogs();
        return () => {
            cancelled = true;
        };
    }, [apiToken, activeTab, auditOffset, isAdmin]);

    const heading = useMemo(() => {
        if (filter === "all") return "All requests";
        return `${filter.charAt(0).toUpperCase()}${filter.slice(1)} requests`;
    }, [filter]);

    const counts = useMemo(() => {
        return requests.reduce(
            (acc, item) => {
                acc[item.status] = (acc[item.status] || 0) + 1;
                acc.all += 1;
                return acc;
            },
            { pending: 0, approved: 0, denied: 0, all: 0 }
        );
    }, [requests]);

    const auditPageStart = auditPagination.total === 0 ? 0 : auditPagination.offset + 1;
    const auditPageEnd = Math.min(auditPagination.offset + auditLogs.length, auditPagination.total);

    function goToPreviousAuditPage() {
        setAuditOffset((current) => Math.max(0, current - AUDIT_PAGE_SIZE));
    }

    function goToNextAuditPage() {
        if (!auditPagination.has_next) return;
        setAuditOffset((current) => current + AUDIT_PAGE_SIZE);
    }

    function startDiscordLogin() {
        const url = getDiscordAuthorizeUrl();
        if (!url) {
            setError("Discord login is not configured.");
            return;
        }
        rememberReturnPath("/admin");
        window.location.href = url;
    }

    function signOut() {
        clearStoredAuth();
        setApiToken(null);
        setProfile(null);
        setRequests([]);
        setAuditLogs([]);
        setAuditOffset(0);
    }

    async function reviewRequest(requestId, action) {
        setActionId(`${requestId}:${action}`);
        setError("");
        try {
            const payload = await apiFetch(`/api/auth/admin/access-requests/${requestId}`, {
                method: "PATCH",
                token: apiToken,
                body: { action },
            });
            setRequests((current) => {
                if (filter !== "all" && payload.request.status !== filter) {
                    return current.filter((item) => item.id !== requestId);
                }
                return current.map((item) => item.id === requestId ? payload.request : item);
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setActionId(null);
        }
    }

    if (!apiToken) {
        return <AdminSignIn error={error} onSignIn={startDiscordLogin} />;
    }

    if (loadingProfile) {
        return (
            <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 dark:bg-slate-900">
                <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    Loading admin session...
                </div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="grid min-h-dvh place-items-center bg-slate-50 px-4 py-8 dark:bg-slate-900">
                <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <h1 className="text-2xl font-bold tracking-normal text-slate-900 dark:text-white">
                        Admin access required
                    </h1>
                    <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        {profile?.user ? `${profile.user.global_name || profile.user.username} is signed in, but is not an admin.` : "This session is not an admin session."}
                    </p>
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <Link to="/" className="rounded-lg bg-indigo-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-600">
                            Back to search
                        </Link>
                        <button
                            type="button"
                            onClick={signOut}
                            className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-600 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-indigo-400"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-white">
            <header className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
                <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase text-slate-400">Admin</div>
                        <h1 className="mt-1 text-3xl font-bold tracking-normal">Admin</h1>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Signed in as {profile?.user?.global_name || profile?.user?.username || profile?.user?.id}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-600 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-indigo-400">
                            Search
                        </Link>
                        <button
                            type="button"
                            onClick={signOut}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-600 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-indigo-400"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6">
                <div className="inline-grid grid-cols-2 rounded-lg border border-slate-200 bg-white p-1 text-sm font-bold shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    {ADMIN_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`rounded-md px-4 py-2 transition ${
                                activeTab === tab.id
                                    ? "bg-indigo-500 text-white"
                                    : "text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === "requests" && (
                    <>
                        <div className="grid gap-3 sm:grid-cols-3">
                            {["pending", "approved", "denied"].map((status) => (
                                <div key={status} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                                    <div className="text-xs font-bold uppercase text-slate-400">{status}</div>
                                    <div className="mt-2 text-2xl font-bold">{counts[status] || 0}</div>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <h2 className="text-xl font-bold tracking-normal">{heading}</h2>
                            <div className="inline-grid grid-cols-4 rounded-lg border border-slate-200 bg-white p-1 text-sm font-bold shadow-sm dark:border-slate-700 dark:bg-slate-800">
                                {STATUS_FILTERS.map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        onClick={() => setFilter(item)}
                                        className={`rounded-md px-3 py-1.5 capitalize transition ${
                                            filter === item
                                                ? "bg-indigo-500 text-white"
                                                : "text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
                                        }`}
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                                {error}
                            </div>
                        )}

                        {loadingRequests ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                Loading requests...
                            </div>
                        ) : requests.length === 0 ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                No requests in this view.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {requests.map((item) => (
                                    <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-indigo-200 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-indigo-800">
                                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                                            <img
                                                src={getDiscordAvatarUrl(item)}
                                                alt=""
                                                className="h-12 w-12 rounded-lg bg-slate-200 object-cover dark:bg-slate-700"
                                                onError={(event) => {
                                                    event.currentTarget.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                                                }}
                                            />
                                            <div className="min-w-0 flex-1 space-y-3">
                                                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                                    <div className="min-w-0">
                                                        <h3 className="truncate font-bold tracking-normal">
                                                            {formatName(item)}
                                                        </h3>
                                                        <div className="break-all text-xs font-medium text-slate-500 dark:text-slate-400">
                                                            @{item.username} · {item.discord_id}
                                                        </div>
                                                    </div>
                                                    <span className={`self-start rounded-md border px-2.5 py-1 text-xs font-bold uppercase ${statusBadge(item.status)}`}>
                                                        {item.status}
                                                    </span>
                                                </div>

                                                <p className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-sm leading-6 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                                                    {item.request_text}
                                                </p>

                                                <div className="grid gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 sm:grid-cols-2">
                                                    <div>Submitted: {formatDate(item.created_at)}</div>
                                                    <div>Reviewed: {formatDate(item.reviewed_at)}</div>
                                                    {item.reviewer_id && <div className="break-all">Reviewer: {item.reviewer_id}</div>}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 lg:w-36 lg:grid-cols-1">
                                                {item.status !== "approved" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => reviewRequest(item.id, "approve")}
                                                        disabled={Boolean(actionId)}
                                                        className="rounded-lg bg-indigo-500 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                                                    >
                                                        {actionId === `${item.id}:approve` ? "..." : item.status === "denied" ? "Grant access" : "Approve"}
                                                    </button>
                                                )}
                                                {item.status !== "denied" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => reviewRequest(item.id, "deny")}
                                                        disabled={Boolean(actionId)}
                                                        className="rounded-lg bg-red-500 px-3 py-2 text-sm font-bold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                                                    >
                                                        {actionId === `${item.id}:deny` ? "..." : item.status === "approved" ? "Revoke access" : "Deny"}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === "audit" && (
                    <>
                        <div className="flex flex-col gap-1">
                            <h2 className="text-xl font-bold tracking-normal">Search audit log</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Recent searches with request metadata and Discord identity when available.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                Showing {auditPageStart}-{auditPageEnd} of {auditPagination.total}
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:w-56">
                                <button
                                    type="button"
                                    onClick={goToPreviousAuditPage}
                                    disabled={loadingAudit || !auditPagination.has_previous}
                                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-45 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-indigo-400"
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={goToNextAuditPage}
                                    disabled={loadingAudit || !auditPagination.has_next}
                                    className="rounded-lg bg-indigo-500 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:bg-slate-300 dark:disabled:bg-slate-700"
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                                {error}
                            </div>
                        )}

                        {loadingAudit ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                Loading audit log...
                            </div>
                        ) : auditLogs.length === 0 ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                                No searches have been logged yet.
                            </div>
                        ) : (
                            <div className="max-h-[72vh] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {auditLogs.map((item) => {
                                        const flag = countryFlag(item.country_code);
                                        const country = countryName(item.country_code);

                                        return (
                                            <article key={item.id} className="p-4">
                                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                                    <div className="min-w-0 flex-1 space-y-3">
                                                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                                            <div className="min-w-0">
                                                                <h3 className="truncate text-sm font-bold tracking-normal">
                                                                    {item.content ? `"${item.content}"` : "Empty search"}
                                                                </h3>
                                                                <div className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    {formatDate(item.created_at)} · {formatDuration(item.duration_ms)} · {item.result_count ?? 0} results
                                                                </div>
                                                            </div>
                                                            <span className="self-start rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold uppercase text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
                                                                {item.search_backend || "unknown"}
                                                            </span>
                                                        </div>

                                                        <div className="grid gap-3 text-sm lg:grid-cols-3">
                                                            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-700">
                                                                <div className="text-xs font-bold uppercase text-slate-400">Discord</div>
                                                                <div className="mt-1 break-all font-semibold text-slate-700 dark:text-slate-200">
                                                                    {formatAuditName(item)}
                                                                </div>
                                                                {item.discord_id && (
                                                                    <div className="mt-1 break-all text-xs text-slate-500 dark:text-slate-400">
                                                                        {item.discord_id}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-700">
                                                                <div className="text-xs font-bold uppercase text-slate-400">Network</div>
                                                                <div className="mt-1 break-all font-semibold text-slate-700 dark:text-slate-200">
                                                                    {item.ip_address || "Unknown IP"}
                                                                </div>
                                                                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                                    {flag && <span className="mr-1">{flag}</span>}
                                                                    {country}
                                                                </div>
                                                            </div>
                                                            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-700">
                                                                <div className="text-xs font-bold uppercase text-slate-400">Filters</div>
                                                                <div className="mt-1 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                                    <div className="break-all">User ID: {item.author || "Any"}</div>
                                                                    <div className="break-all">Username: {item.author_username || "Any"}</div>
                                                                    <div>Start: {formatOptionalDate(item.start_time)}</div>
                                                                    <div>End: {formatOptionalDate(item.end_time)}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="break-words rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                                            {userAgentSummary(item.user_agent)}
                                                        </div>

                                                        {item.error && (
                                                            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                                                                {item.error}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
